/**
 * THE WORDS FOR "HOW DID THIS DEAL END" — every string the report flow shows, EN/ES.
 *
 * The screen talks about the user's benefit — their own record of how close the analysis
 * landed — never about improving a model or collecting data. And it shows the MAGNITUDE
 * of the miss ("landed within 2.4% of the real value"), never the sign: the sign is stored
 * with the outcome for the audit, and is not the user's concern.
 *
 * Pure: strings in, strings out, so it is tested as a table.
 */

export const OUTCOME_TYPES = ['sold', 'refi_appraisal', 'no_deal'];
export const NO_DEAL_REASONS = ['not_purchased', 'sold_to_other', 'still_listed'];

const en = (lang) => lang === 'en';
const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`;

/** The step-1 question and its three answers. */
export function step1Text(lang) {
  return en(lang)
    ? {
        title: 'How did this deal end?',
        lead: "Tell us what happened and you'll see how close your analysis came.",
        options: {
          sold: 'It sold',
          refi_appraisal: 'I refinanced (new appraisal)',
          no_deal: "It didn't close",
        },
      }
    : {
        title: '¿Cómo terminó este trato?',
        lead: 'Dinos qué pasó y verás qué tan cerca estuvo tu análisis.',
        options: {
          sold: 'Se vendió',
          refi_appraisal: 'Refinancié (tasación nueva)',
          no_deal: 'No cerró',
        },
      };
}

/** The step-2 prompt for the chosen type. */
export function step2Text(type, lang) {
  const e = en(lang);
  if (type === 'sold') {
    return {
      amountLabel: e ? 'What did it sell for?' : '¿En cuánto se vendió?',
      dateLabel: e ? 'Closing date (optional)' : 'Fecha de cierre (opcional)',
    };
  }
  if (type === 'refi_appraisal') {
    return {
      amountLabel: e ? 'What value did the appraiser give it?' : '¿Qué valor le dio el tasador?',
      dateLabel: e ? 'Appraisal date (optional)' : 'Fecha de la tasación (opcional)',
    };
  }
  return {
    reasonLabel: e ? 'What happened?' : '¿Qué pasó?',
    reasons: e
      ? { not_purchased: "I didn't buy it", sold_to_other: 'Someone else bought it', still_listed: "It's still on the market" }
      : { not_purchased: 'No lo compré', sold_to_other: 'Vendió otro', still_listed: 'Sigue en el mercado' },
  };
}

/**
 * After saving. `comparison` is { predicted, actual, diff_pct } or null; `trackRecord`
 * is { reports, mean_abs_diff_pct } or null. The average is shown only with two or
 * more reports — one report has no average.
 */
export function resultText(type, comparison, trackRecord, lang) {
  const e = en(lang);
  if (type === 'no_deal' || !comparison) {
    return { title: e ? 'Thanks, recorded.' : 'Gracias, registrado.', lines: [] };
  }
  const miss = Math.abs(Number(comparison.diff_pct) || 0);
  const lines = [
    e
      ? `Your analysis predicted ${money(comparison.predicted)}. Real result: ${money(comparison.actual)}.`
      : `Tu análisis predijo ${money(comparison.predicted)}. Resultado real: ${money(comparison.actual)}.`,
    e
      ? `Your analysis landed within ${miss.toFixed(1)}% of the real value.`
      : `Tu análisis quedó a ${miss.toFixed(1)}% del valor real.`,
  ];
  if (trackRecord && Number(trackRecord.reports) >= 2) {
    const avg = Number(trackRecord.mean_abs_diff_pct) || 0;
    lines.push(
      e
        ? `Across your ${trackRecord.reports} reported deals, your analyses land within ${avg.toFixed(1)}% on average.`
        : `En tus ${trackRecord.reports} tratos reportados, tus análisis quedan a ${avg.toFixed(1)}% en promedio.`,
    );
  }
  return { title: e ? 'Recorded.' : 'Registrado.', lines };
}

/** The chip on a card that already has an outcome. */
export function outcomeChip(outcome, lang) {
  if (!outcome || !outcome.type) return '';
  const e = en(lang);
  const c = outcome.comparison;
  const miss = c && Number.isFinite(Number(c.diff_pct)) ? Math.abs(Number(c.diff_pct)).toFixed(1) : null;
  if (outcome.type === 'sold') {
    return miss != null
      ? (e ? `Sold · within ${miss}%` : `Vendido · a ${miss}%`)
      : (e ? 'Sold' : 'Vendido');
  }
  if (outcome.type === 'refi_appraisal') {
    return miss != null
      ? (e ? `Appraised · within ${miss}%` : `Tasado · a ${miss}%`)
      : (e ? 'Appraised' : 'Tasado');
  }
  const reasons = step2Text('no_deal', lang).reasons;
  return reasons[outcome.no_deal_reason] || (e ? "Didn't close" : 'No cerró');
}

/** Buttons and errors. */
export function uiText(lang) {
  return en(lang)
    ? {
        report: 'Report result',
        reported: '✓ Reported',
        next: 'Next',
        back: 'Back',
        save: 'Save',
        saving: 'Saving…',
        close: 'Close',
        amountRequired: 'Enter the amount.',
        reasonRequired: 'Pick what happened.',
        failed: "Couldn't save. Try again.",
      }
    : {
        report: 'Reportar resultado',
        reported: '✓ Reportado',
        next: 'Siguiente',
        back: 'Atrás',
        save: 'Guardar',
        saving: 'Guardando…',
        close: 'Cerrar',
        amountRequired: 'Ingresa el monto.',
        reasonRequired: 'Elige qué pasó.',
        failed: 'No se pudo guardar. Intenta de nuevo.',
      };
}

/** The payload for PATCH /api/analyses/{id}/outcome, or { error } if the form is not ready. */
export function buildOutcomePayload({ type, amount, date, reason }, lang) {
  const t = uiText(lang);
  if (!OUTCOME_TYPES.includes(type)) return { error: t.reasonRequired };
  if (type === 'no_deal') {
    if (!NO_DEAL_REASONS.includes(reason)) return { error: t.reasonRequired };
    return { payload: { outcome_type: 'no_deal', no_deal_reason: reason } };
  }
  const n = Number(String(amount ?? '').replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return { error: t.amountRequired };
  const payload = { outcome_type: type, amount: n };
  if (date) payload.date = date;
  return { payload };
}
